import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Star, ArrowRight } from 'lucide-react';
import { mockData } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';

export const Home = () => {
  const carouselOpts = {
    align: "start",
    loop: true,
  };

  const successStories = [
    {
      image: 'https://customer-assets.emergentagent.com/job_2343b8d4-bf7c-477c-9f5a-ad0017c87460/artifacts/yg3mqisk_IMG_1100.jpeg',
      alt: 'Happy student celebrating driving test pass'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_2343b8d4-bf7c-477c-9f5a-ad0017c87460/artifacts/e4wtyxx5_IMG_1118.jpeg',
      alt: 'Successful student with pass certificate'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_2343b8d4-bf7c-477c-9f5a-ad0017c87460/artifacts/89hz25ay_IMG_1116.jpeg',
      alt: 'Student passed driving test with Top Driver'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_2343b8d4-bf7c-477c-9f5a-ad0017c87460/artifacts/jatldrqv_IMG_1114.jpeg',
      alt: 'Another successful driving test pass'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_2343b8d4-bf7c-477c-9f5a-ad0017c87460/artifacts/w9cqwkiw_IMG_1111.jpeg',
      alt: 'Proud student with driving pass certificate'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Driving Lessons Liverpool | Automatic Driving Instructor | Top Driver"
        description="Professional automatic driving lessons in Liverpool with a friendly DVSA-approved instructor. High pass rate, flexible times, and affordable prices. Book today with Top Driver."
        keywords="automatic driving lessons Liverpool, automatic driving school Liverpool, learn to drive automatic Liverpool, automatic driving instructor Liverpool, automatic driving test Liverpool, Liverpool automatic lessons, intensive automatic crash course Liverpool"
        canonical="https://topdriver.co.uk"
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-black mb-6 leading-tight">
              {mockData.hero.title}
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {mockData.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg transition-colors duration-200"
              >
                <Link to="/contact">{mockData.hero.cta}</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-black text-black hover:bg-black hover:text-white px-8 py-6 text-lg transition-colors duration-200"
              >
                <a href="#pricing">{mockData.hero.ctaSecondary}</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Why choose Top Driver Driving School?</h2>
            <p className="text-lg text-gray-600">Experience the difference with our proven approach</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {mockData.whyChooseUs.map((item) => (
              <Card key={item.title} className="border-2 border-gray-200 hover:border-red-600 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-2">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Intensive Crash Course - Featured Payment Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-5xl mx-auto">
            <Card className="border-4 border-red-600 shadow-2xl overflow-hidden">
              <div className="bg-red-600 text-white py-3 px-6 text-center">
                <p className="font-bold text-lg">{mockData.intensiveCrashCourse.highlight}</p>
              </div>
              <CardContent className="p-8 md:p-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Side - Course Info */}
                  <div>
                    <h2 className="text-4xl font-bold text-black mb-2">
                      {mockData.intensiveCrashCourse.name}
                    </h2>
                    <p className="text-lg text-gray-600 mb-4">
                      {mockData.intensiveCrashCourse.description}
                    </p>
                    <div className="mb-6">
                      <div className="flex items-baseline gap-3">
                        <span className="text-5xl font-bold text-black">
                          {mockData.intensiveCrashCourse.price}
                        </span>
                        <span className="text-2xl text-gray-400 line-through">
                          {mockData.intensiveCrashCourse.originalPrice}
                        </span>
                      </div>
                      <p className="text-gray-600 mt-1">{mockData.intensiveCrashCourse.duration}</p>
                    </div>
                    <ul className="space-y-2 mb-4">
                      {mockData.intensiveCrashCourse.features.map((feature) => (
                        <li key={feature} className="flex items-start space-x-2">
                          <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                      {/* Bonus Feature - Highlighted */}
                      <li className="flex items-start space-x-2 bg-green-50 border border-green-200 rounded-lg p-2 -mx-2">
                        <span className="text-green-600 mt-0.5 flex-shrink-0 text-lg">✓</span>
                        <span className="text-green-700 font-semibold text-sm">{mockData.intensiveCrashCourse.bonusFeature}</span>
                      </li>
                    </ul>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      *Practical test fee is included for learners who complete the full 30-hour course with Top Driver Driving School. The offer applies to the first practical test booked through the course.
                    </p>
                  </div>

                  {/* Right Side - CTA */}
                  <div className="flex flex-col justify-center">
                    <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-lg">
                      <h3 className="text-2xl font-bold text-black mb-4 text-center">
                        Ready to Get Started?
                      </h3>
                      <p className="text-gray-600 text-center mb-6">
                        Contact us today to book your intensive course and pass your test quickly!
                      </p>
                      <Button
                        asChild
                        size="lg"
                        className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg transition-colors duration-200 mb-4"
                      >
                        <Link to="/contact">
                          Book Now - {mockData.intensiveCrashCourse.price}
                        </Link>
                      </Button>
                      <p className="text-sm text-gray-500 text-center">
                        0% Finance available via Klarna
                      </p>
                    </div>
                    <p className="text-center text-sm text-gray-600 mt-4">
                      Questions? <Link to="/contact" className="text-red-600 hover:underline">Contact us</Link> for more information
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Success Stories Carousel */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Our Success Stories</h2>
            <p className="text-lg text-gray-600">Real students, real results. Join our community of confident drivers!</p>
          </div>
          <div className="max-w-5xl mx-auto">
            <Carousel
              opts={carouselOpts}
              className="w-full"
            >
              <CarouselContent>
                {successStories.map((story) => (
                  <CarouselItem key={story.image} className="md:basis-1/2 lg:basis-1/3">
                    <div className="p-2">
                      <Card className="border-2 border-gray-200 hover:border-red-600 transition-colors duration-300 overflow-hidden">
                        <CardContent className="p-0">
                          <div className="aspect-square relative overflow-hidden">
                            <img
                              src={story.image}
                              alt={story.alt}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="border-2 border-gray-300 hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors duration-200" />
              <CarouselNext className="border-2 border-gray-300 hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors duration-200" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">Choose Your Package</h2>
            <p className="text-lg text-gray-600">Flexible options to fit your needs and budget</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.pricingPlans.map((plan) => (
              <Card
                key={plan.packageId}
                className={`relative border-2 transition-all duration-300 ${
                  plan.popular
                    ? 'border-red-600 shadow-xl scale-105'
                    : 'border-gray-200 hover:border-red-600 hover:shadow-lg'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-black mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-black">{plan.price}</span>
                    <span className="text-gray-600 text-sm ml-1">/ {plan.duration}</span>
                  </div>
                  <ul className="space-y-2 mb-4">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start space-x-2">
                        <CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-xs">{feature}</span>
                      </li>
                    ))}
                    {plan.bonusFeature && (
                      <li className="flex items-start space-x-2 bg-green-50 border border-green-200 rounded-lg p-2 -mx-1">
                        <span className="text-green-600 mt-0.5 flex-shrink-0">✓</span>
                        <span className="text-green-700 font-semibold text-xs">{plan.bonusFeature}</span>
                      </li>
                    )}
                  </ul>
                  {plan.bonusFeature && (
                    <p className="text-[10px] text-gray-500 mb-4 leading-relaxed">
                      *Practical test fee included for learners completing the full 30-hour course. Applies to first test booked through the course.
                    </p>
                  )}
                  <div className="space-y-2">
                    <Button
                      asChild
                      className={`w-full transition-colors duration-200 ${
                        plan.popular
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-black hover:bg-gray-800 text-white'
                      }`}
                    >
                      <Link to="/contact">Book Now</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <svg className="w-8 h-8" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <h2 className="text-4xl font-bold text-black">Google Reviews</h2>
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star key={`header-star-${i}`} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              ))}
              <span className="text-xl font-bold text-black ml-2">5.0</span>
            </div>
            <p className="text-lg text-gray-600">Rated 5 stars from 8 reviews on Google</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {/* Review 1 */}
            <Card className="border-2 border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed text-sm">"Arek is an amazing instructor, he made me feel relaxed and comfortable from my very first lesson! He was extremely professional and highly knowledgeable. I passed first time with the help of him. I'd highly recommend!"</p>
                <div className="border-t border-gray-200 pt-3">
                  <p className="font-semibold text-black">Brenna Donnelly</p>
                  <p className="text-xs text-gray-500">Google Review</p>
                </div>
              </CardContent>
            </Card>

            {/* Review 2 */}
            <Card className="border-2 border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed text-sm">"Best instructor in Liverpool! Highly professional, always punctual, very patient and polite. Arek is also a great teacher. Passed on 1st attempt!"</p>
                <div className="border-t border-gray-200 pt-3">
                  <p className="font-semibold text-black">Edyta Janaszek</p>
                  <p className="text-xs text-gray-500">Google Review</p>
                </div>
              </CardContent>
            </Card>

            {/* Review 3 */}
            <Card className="border-2 border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed text-sm">"Arek is a very good instructor gave me lots of confidence. I passed first time because of him. Couldn't recommend him enough, amazing instructor!"</p>
                <div className="border-t border-gray-200 pt-3">
                  <p className="font-semibold text-black">Harvey</p>
                  <p className="text-xs text-gray-500">Google Review</p>
                </div>
              </CardContent>
            </Card>

            {/* Review 4 */}
            <Card className="border-2 border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed text-sm">"Arek is very professional, reliable, and at the same time friendly and patient. I went to every lesson not feeling stressed, but confident that I would learn something new. Calm, focused, and truly experienced, with a great approach to the student."</p>
                <div className="border-t border-gray-200 pt-3">
                  <p className="font-semibold text-black">Ania Sokołowska</p>
                  <p className="text-xs text-gray-500">Google Review</p>
                </div>
              </CardContent>
            </Card>

            {/* Review 5 */}
            <Card className="border-2 border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed text-sm">"The best driving instructor ever. His teaching style is very patient and calm. He helped me to build my confidence and made me feel comfortable behind the wheel!"</p>
                <div className="border-t border-gray-200 pt-3">
                  <p className="font-semibold text-black">Emilia Rumenova</p>
                  <p className="text-xs text-gray-500">Google Review</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* See All Reviews Button */}
          <div className="text-center">
            <Button
              asChild
              size="lg"
              className="bg-white hover:bg-gray-100 text-gray-800 border-2 border-gray-300 hover:border-gray-400 px-8 py-6 text-lg transition-all duration-200"
            >
              <a 
                href="https://g.page/r/CXF8KsSkD_GpEAE/review" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                See All Google Reviews
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Everything you need to know about learning to drive with Top Driver</p>
          </div>
          
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1" className="border-b border-gray-200">
              <AccordionTrigger className="text-left text-lg font-semibold text-black hover:text-red-600 hover:no-underline py-5">
                Do you offer automatic driving lessons in Liverpool?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base pb-5">
                Yes, Top Driver specialises in automatic driving lessons in Liverpool. Automatic cars are easier to learn in because there is no clutch or gear changes, helping learners gain confidence faster.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border-b border-gray-200">
              <AccordionTrigger className="text-left text-lg font-semibold text-black hover:text-red-600 hover:no-underline py-5">
                How much do driving lessons cost in Liverpool?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base pb-5">
                Our driving lesson prices are competitive and offer excellent value. We charge £45 for an hour session. That's the cheapest offer in Liverpool as other schools charge £50 per hour or more.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border-b border-gray-200">
              <AccordionTrigger className="text-left text-lg font-semibold text-black hover:text-red-600 hover:no-underline py-5">
                How many driving lessons will I need to pass?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base pb-5">
                The number of lessons depends on your previous experience and confidence level. On average, most learners need between 25-40 hours of lessons to reach test standard.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border-b border-gray-200">
              <AccordionTrigger className="text-left text-lg font-semibold text-black hover:text-red-600 hover:no-underline py-5">
                Do you teach beginner drivers?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base pb-5">
                Yes, we welcome complete beginners as well as learners with some experience. Lessons are structured to help you build skills step by step in a calm and supportive environment.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border-b border-gray-200">
              <AccordionTrigger className="text-left text-lg font-semibold text-black hover:text-red-600 hover:no-underline py-5">
                Are you a DVSA approved driving instructor?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base pb-5">
                Yes, Top Driver is a DVSA-approved driving instructor, providing professional and safe driving lessons in Liverpool.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border-b border-gray-200">
              <AccordionTrigger className="text-left text-lg font-semibold text-black hover:text-red-600 hover:no-underline py-5">
                Do you offer intensive driving courses?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base pb-5">
                We can arrange intensive or semi-intensive driving lessons depending on your availability and learning speed.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="border-b border-gray-200">
              <AccordionTrigger className="text-left text-lg font-semibold text-black hover:text-red-600 hover:no-underline py-5">
                Which areas in Liverpool do you cover?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base pb-5">
                We provide driving lessons across Liverpool including Anfield, Everton, Bootle, Walton, Tuebrook, Kensington and Norris Green area.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" className="border-b border-gray-200">
              <AccordionTrigger className="text-left text-lg font-semibold text-black hover:text-red-600 hover:no-underline py-5">
                Can nervous drivers learn with you?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base pb-5">
                Yes, we specialise in helping nervous drivers feel relaxed and confident behind the wheel with patient and friendly teaching methods.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-9" className="border-b border-gray-200">
              <AccordionTrigger className="text-left text-lg font-semibold text-black hover:text-red-600 hover:no-underline py-5">
                Do you help prepare for the driving test?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base pb-5">
                Yes, we provide mock driving tests and full preparation so you feel confident and ready for your practical driving test.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-10" className="border-b border-gray-200">
              <AccordionTrigger className="text-left text-lg font-semibold text-black hover:text-red-600 hover:no-underline py-5">
                How can I book driving lessons?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base pb-5">
                You can book lessons by contacting us through the website or by phone. We will arrange a suitable time based on your availability.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-red-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to start your Driving Journey?
          </h2>
          <p className="text-xl text-white mb-8 opacity-90">
            Book your first lesson today and join thousands of confident drivers.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white hover:bg-gray-100 text-red-600 px-8 py-6 text-lg transition-colors duration-200"
          >
            <Link to="/contact" className="inline-flex items-center space-x-2">
              <span>Schedule Your Lesson</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;